import {
  isColumnarSheet,
  type WorkbookDefinition,
  type WorkbookDiagnostic,
  type WorkbookDistinctValues,
  type WorkbookPivotSheet,
  type WorkbookResult,
  type WorkbookTable,
} from '@erp/shared-types';
import {
  columnReference,
  suggestColumnType,
  type ColumnType,
  type Schema,
  type WorkbookPlan,
} from '@erp/workbooks';
import {
  fieldDistinctValues,
  type ShownDistinctValues,
} from '../sampleDistinctValues';
import { isSetupDiagnostic } from '../WorksheetDiagnostics';
import { worksheetReadableTables } from '../worksheetReadableTables';

/** An input Field as the pivot editor offers it. */
export type PivotField = {
  name: string;
  type: ColumnType;
  /** Its values read as numbers, so a Value sums it: a Number, or Text holding only numbers. */
  numeric: boolean;
  /** Its Distinct values, for a Filter's value list, when a Result or preview (a sample) has them. */
  values?: ShownDistinctValues;
};

/**
 * The Fields a pivot can use: its input's planned schema, else a preview's
 * columns as Text (every Source column is Text). Their Distinct values come
 * from a Result (the pivot's own, recording its input's), else a sample of
 * the preview's first rows.
 */
export function pivotFields({
  planned,
  preview,
  distinct,
}: {
  planned?: Schema | null;
  preview?: WorkbookTable;
  distinct?: readonly WorkbookDistinctValues[];
}): PivotField[] {
  const schema: Schema =
    planned ?? preview?.columns.map((name) => ({ name, type: 'text' })) ?? [];
  return schema.map(({ name, type }) => {
    // A Run reads only the fields a pivot uses: the rest come from the preview.
    const values = fieldDistinctValues(distinct, name, preview);
    const sample = values?.values.map((entry) => entry.value) ?? [];
    return {
      name,
      type,
      numeric:
        type === 'decimal' ||
        (type === 'text' &&
          sample.length > 0 &&
          suggestColumnType(sample) === 'decimal'),
      values,
    };
  });
}

/**
 * What the pivot tab shows around its Result: the Fields its editor offers
 * (Distinct values from its own Result, else its input Worksheet's), the
 * tables it can summarize (every Source and Worksheet but itself), and its
 * Diagnostics.
 */
export function pivotPanelData({
  sheet,
  definition,
  plan,
  result,
  inputResult,
  input,
}: {
  sheet: WorkbookPivotSheet;
  definition: WorkbookDefinition;
  plan: WorkbookPlan;
  result?: WorkbookResult;
  inputResult?: WorkbookResult;
  input?: WorkbookTable;
}): {
  fields: PivotField[];
  inputs: { id: string; name: string }[];
  diagnostics: WorkbookDiagnostic[];
  /** It has no fields yet: a next step to show, not a Diagnostic to fix. */
  unfinished: boolean;
} {
  const own = plan.diagnostics.filter(
    (diagnostic) => diagnostic.location.itemId === sheet.id,
  );
  return {
    fields: pivotFields({
      planned: plan.sheets.get(sheet.id)?.pivot?.input,
      preview: input,
      distinct: result?.distinctValues ?? inputResult?.distinctValues,
    }),
    inputs: worksheetReadableTables(definition, sheet.id),
    diagnostics: own.filter((diagnostic) => !isSetupDiagnostic(diagnostic)),
    unfinished: own.some(isSetupDiagnostic),
  };
}

/**
 * A preview of a columnar Worksheet that has no Result yet, from its inline
 * Source: the columns it copies unchanged (`[Name]`), row for row. Enough to
 * tell a numeric field before the first Run; undefined for anything else.
 */
export function upstreamPreview(
  definition: WorkbookDefinition,
  sheetId: string,
): WorkbookTable | undefined {
  const sheet = definition.sheets.find((item) => item.id === sheetId);
  if (!isColumnarSheet(sheet)) return undefined;
  const source = definition.sources.find((item) => item.id === sheet.inputId);
  if (source?.kind !== 'input') return undefined;
  const copied = sheet.columns.flatMap((column) => {
    const index = source.table.columns.indexOf(column.name);
    return index >= 0 &&
      column.expression.trim() === columnReference(column.name)
      ? [{ name: column.name, index }]
      : [];
  });
  if (!copied.length) return undefined;
  return {
    columns: copied.map((column) => column.name),
    rows: source.table.rows.map((row) =>
      copied.map((column) => row[column.index] ?? null),
    ),
  };
}
